import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { reconcilePayments } from "@/lib/reconciliation.functions";
import { retryShiftCapture } from "@/lib/payments.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/_authenticated/admin/reconciliation")({
  head: () => ({ meta: [{ title: "Payment reconciliation — Admin" }] }),
  component: ReconciliationPage,
  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive">{error.message}</div>,
  notFoundComponent: () => <div className="p-8">Not found.</div>,
});

type Row = {
  booking_id: string; shift_id: string; shift_start: string | null;
  payment_intent_id: string | null; app_status: string; stripe_status: string | null;
  match: "ok" | "missing_pi" | "stripe_mismatch" | "stripe_error";
  capture_status: string | null; captured_amount_pence: number | null;
  last_captured_at: string | null;
  capture_idempotency_key: string | null; capture_error: string | null;
};

async function copy(text: string | null | undefined, label: string) {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch { toast.error("Copy failed"); }
}

function CopyChip({ value, label }: { value: string | null; label: string }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <button
      type="button"
      onClick={() => copy(value, label)}
      title={`Click to copy ${label}`}
      className="inline-flex items-center gap-1 rounded border border-border bg-muted/30 px-1.5 py-0.5 font-mono text-[10px] hover:bg-muted"
    >
      <span className="max-w-[110px] truncate">{value}</span>
      <span aria-hidden className="opacity-60">⧉</span>
    </button>
  );
}

function ReconciliationPage() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [rows, setRows] = useState<Row[]>([]);

  const mut = useMutation({
    mutationFn: reconcilePayments,
    onSuccess: (res) => {
      if ("error" in res) { toast.error(res.error); return; }
      setRows(res.rows as Row[]);
      toast.success(`Reconciled ${res.rows.length} bookings.`);
    },
  });

  const retryMut = useMutation({
    mutationFn: (shift_id: string) => retryShiftCapture({ data: { shift_id } }),
    onSuccess: (res, shift_id) => {
      if ("error" in res) { toast.error(res.error); return; }
      toast.success(`Capture ${res.capture_status}.`);
      setRows((prev) => prev.map((r) => r.shift_id === shift_id
        ? { ...r, capture_status: res.capture_status, captured_amount_pence: res.captured_amount_pence, capture_error: null }
        : r));
    },
  });

  const counts = rows.reduce<Record<string, number>>((acc, r) => {
    acc[r.match] = (acc[r.match] ?? 0) + 1; return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <p className="text-[11px] tracking-wide-luxury text-muted-foreground">Admin · Finance</p>
        <h1 className="text-3xl font-light">Payment reconciliation</h1>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          Matches every booking marked <em>paid</em> in the app against its Stripe payment intent.
          Anything other than <em>ok</em> needs investigation.
        </p>

        <div className="mt-6 grid gap-4 border border-border p-6 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <div><Label>From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
          <div><Label>To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
          <Button onClick={() => mut.mutate({ data: { from, to } })} disabled={mut.isPending}>{mut.isPending ? "Running…" : "Run reconciliation"}</Button>
        </div>

        {rows.length > 0 && (
          <>
            <div className="mt-6 flex flex-wrap gap-3 text-xs tracking-wide-luxury">
              {(["ok", "missing_pi", "stripe_mismatch", "stripe_error"] as const).map((k) => (
                <span key={k} className="border border-border px-3 py-2">{k}: {counts[k] ?? 0}</span>
              ))}
            </div>
            <div className="mt-4 overflow-x-auto border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/30 text-left tracking-wide-luxury">
                  <tr>
                    <th className="p-3">Booking</th>
                    <th className="p-3">Shift</th>
                    <th className="p-3">PI</th>
                    <th className="p-3">Idem key</th>
                    <th className="p-3">App</th>
                    <th className="p-3">Stripe</th>
                    <th className="p-3">Match</th>
                    <th className="p-3">Capture</th>
                    <th className="p-3">Captured</th>
                    <th className="p-3">Last captured</th>
                    <th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const canRetry = r.capture_status === "failed" || r.capture_status === "pending";
                    return (
                      <tr key={r.booking_id} className={`border-t border-border ${r.match !== "ok" ? "bg-destructive/5" : ""}`}>
                        <td className="p-3 font-mono text-[10px]">{r.booking_id.slice(0, 8)}</td>
                        <td className="p-3">
                          <Link to="/admin/shifts/$id" params={{ id: r.shift_id }} className="text-primary underline-offset-4 hover:underline">
                            {r.shift_start ? new Date(r.shift_start).toLocaleString("en-GB") : r.shift_id.slice(0, 8)}
                          </Link>
                        </td>
                        <td className="p-3"><CopyChip value={r.payment_intent_id} label="PaymentIntent ID" /></td>
                        <td className="p-3"><CopyChip value={r.capture_idempotency_key} label="Idempotency key" /></td>
                        <td className="p-3">{r.app_status}</td>
                        <td className="p-3">{r.stripe_status ?? "—"}</td>
                        <td className="p-3 uppercase tracking-wide-luxury">{r.match}</td>
                        <td className={`p-3 uppercase tracking-wide-luxury ${r.capture_status === "failed" ? "text-destructive" : r.capture_status === "captured" || r.capture_status === "partially_captured" ? "text-emerald-700" : "text-muted-foreground"}`}>
                          {r.capture_status ?? "—"}
                          {r.capture_error && <div className="mt-0.5 normal-case text-[10px] text-destructive">{r.capture_error}</div>}
                        </td>
                        <td className="p-3">{r.captured_amount_pence != null ? `£${(r.captured_amount_pence / 100).toFixed(2)}` : "—"}</td>
                        <td className="p-3">{r.last_captured_at ? new Date(r.last_captured_at).toLocaleString("en-GB") : "—"}</td>
                        <td className="p-3">
                          {canRetry && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={retryMut.isPending}
                              onClick={() => retryMut.mutate(r.shift_id)}
                            >
                              Retry capture
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
